import React from 'react';
import { Link, useNavigate } from 'react-router-dom';
import Header from '../components/Header';
import Footer from '../components/Footer';
import '../styles/theme.css';
import './ErrorPage.css';

// Shared error/status page used by 404 / 401 / 403 / 500 / Maintenance.
// Each variant supplies the code, accent color, title, message, and CTAs.
//
// Props
//   code:        '404' | '401' | '403' | '500' | '503' | string
//   tone:        'default' | 'warn' | 'danger' — drives the accent color
//   eyebrow:     small uppercase label above the code (e.g. 'Not Found')
//   title:       big headline below the code
//   message:     paragraph explaining what happened / what to do
//   primaryCta:  { label, to?, href?, onClick? } — gold solid button
//   secondaryCta:{ label, to?, href?, onClick? } — ghost-gold button
//   showSupport: render the "still stuck?" support row
//   hideChrome:  hide <Header> and <Footer> (use for full-bleed maintenance)
const ErrorPage = ({
  code = '404',
  tone = 'default',
  eyebrow,
  title,
  message,
  primaryCta,
  secondaryCta,
  showSupport = true,
  hideChrome = false,
}) => {
  const navigate = useNavigate();

  const renderCta = (cta, variant) => {
    if (!cta) return null;
    const className = `error-page-btn error-page-btn--${variant}`;
    if (cta.to) return <Link to={cta.to} className={className}>{cta.label}</Link>;
    if (cta.href) return <a href={cta.href} className={className}>{cta.label}</a>;
    return (
      <button type="button" className={className} onClick={cta.onClick || (() => navigate(-1))}>
        {cta.label}
      </button>
    );
  };

  return (
    <div className={`error-page error-page--${tone}`}>
      {!hideChrome && <Header />}

      <main className="error-page-main">
        <div className="error-page-stage">
          {/* Big stylized status code */}
          <div className="error-page-code" aria-hidden="true">
            <span className="error-page-code-text">{code}</span>
            <span className="error-page-code-glow" />
          </div>

          {eyebrow && <div className="error-page-eyebrow">{eyebrow}</div>}
          {title && <h1 className="error-page-title">{title}</h1>}
          {message && <p className="error-page-message">{message}</p>}

          <div className="error-page-actions">
            {renderCta(primaryCta, 'primary')}
            {renderCta(secondaryCta, 'secondary')}
          </div>

          {showSupport && (
            <div className="error-page-support">
              Still stuck? <a href="/dashboard?tab=support">Open a support ticket</a> or
              {' '}
              <a href="https://t.me/phonestreamofficial" target="_blank" rel="noopener noreferrer">
                ping us on Telegram
              </a>.
            </div>
          )}
        </div>
      </main>

      {!hideChrome && <Footer />}
    </div>
  );
};

export default ErrorPage;
